import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlSegment,
} from '@angular/router';
import {
    ChannelAdvancedSettingsFormComponent,
    ChannelAdvancedSettingSubmission,
} from '@components/channel-advanced-settings-form/channel-advanced-settings-form.component';
import { ChannelSettingsFormComponent } from '@components/channel-settings-form/channel-settings-form.component';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { ChannelGroupService } from '@service/channel-groups.service';
import { ChannelService } from '@service/channel.service';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import { CustomGroupsService } from '@service/custom-groups.service';
import { DeviceFlagsService } from '@service/device-flags.service';
import { NavigationService } from '@service/navigation.service';
import { NotificationService } from '@service/notification.service';
import { ResponsiveService } from '@service/responsive.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { Channel } from 'ops/common/models/channel';
import { ChannelGroup } from 'ops/common/models/channel-group';
import { CustomGroup, ChannelCustomGroup } from 'ops/common/models/custom-group';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { CustomGroupAccessSettingsService } from '@service/custom-group-access-settings.service';
import { AccessType, UserAccess } from 'ops/common/models/access-settings';
import { AuthService } from '@service/auth.service';

import { Subscription, lastValueFrom, Observable, firstValueFrom, skip, first } from 'rxjs';
import { OpsNewCustomGroupComponent } from './ops-new-custom-group/ops-new-custom-group.component';
import { LeavePageConfirmationComponent } from '@components/leave-page-confirmation/leave-page-confirmation.component';
import { HttpStatusCode } from '@angular/common/http';
import { VesselService } from '@service/vessel.service';
import { OpsChannelListComponent } from './ops-channel-list/ops-channel-list.component';
import { CustomGroupSocketService } from '@service/custom-group-socket.service';

@Component({
    selector: 'app-channel-views',
    templateUrl: './ops-channel-views.component.html',
    styleUrls: ['./ops-channel-views.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class OpsChannelViewsComponent implements OnInit, OnDestroy {
    readonly VIEWMODE_GROUPS = 'groups';
    readonly VIEWMODE_CHANNELS = 'channels';
    readonly VIEWMODE_ADVANCED_CHANNEL = 'advanced';
    readonly VIEWMODE_NEW_CUSTOM_GROUP = 'newGroup';

    @ViewChild('channelSettingsForm')
    channelSettingsForm: ChannelSettingsFormComponent;

    @ViewChild('channelSettingsListForm')
    channelSettingsListForm: OpsChannelListComponent;

    @ViewChild('confirmationWindow', { static: true })
    confirmationWindow: ConfirmationWindowComponent;

    @ViewChild('deleteConfirmationOverlay', { static: true })
    deleteConfirmOverlay: ConfirmationWindowComponent;

    @ViewChild('advancedChannelSettingsForm')
    advancedChannelSettingsForm: ChannelAdvancedSettingsFormComponent;
    @ViewChild('newCustomGroup') newCustomGroup: OpsNewCustomGroupComponent;
    @ViewChild('exitConfirmOverlay') exitConfirmOverlay: LeavePageConfirmationComponent;

    viewMode: string = this.VIEWMODE_GROUPS;

    parameterSubscription: Subscription;
    urlSubscription: Subscription;
    selectedGroupId: string = null;
    selectedGroup: any = null;
    customName: String;
    customGroup: Boolean;
    customGroupData;
    hasFinishedLoading: boolean = false;

    groups: ChannelGroup[];
    channels: Channel[];
    channelLookup: { [fcuChannelId: number]: Channel };
    customGroups: CustomGroup[];
    filteredCustomGroups: CustomGroup[];
    customGroupChannels: ChannelCustomGroup[];
    subscritptions: Subscription[] = [];
    selectedChannel: Channel;

    displayChannels: Channel[] = [];
    focusedChannel: Channel = null;
    advancedDetails: boolean = false;
    selectedGroupUsersAccess: UserAccess[];

    currentUserSubjectSubscription: Subscription;
    accessUpdatedSubscription: Subscription;

    constructor(
        private groupService: ChannelGroupService,
        private channelService: ChannelService,
        private flagService: DeviceFlagsService,
        private trendGraphService: TrendGraphViewsService,
        private notificationService: NotificationService,
        private navigation: NavigationService,
        private confirmationPromptService: ConfirmationPromptService,
        private translate: TranslateService,
        private customGroupsService: CustomGroupsService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        public responsive: ResponsiveService,
        private authService: AuthService,
        private vesselService: VesselService,
        private customGroupSocketService: CustomGroupSocketService,
    ) {}

    async ngOnInit() {
        this.confirmationPromptService.init({
            window: this.confirmationWindow,
        });

        await this.vesselService.loadVessel();
        await this.loadChannelData();

        this.customGroupSocketService.startSubscriptions();

        this.parameterSubscription = this.route.params.subscribe((params) => {
            const groupId = params['groupId'];
            this.selectedGroupId = groupId;

            this.setSelectedGroup(groupId);

            const rawChannelId = params['fcuChannelId'];
            const viewChannelId = Number(rawChannelId);

            if (rawChannelId == null) {
                this.setFocusedChannel(null);
            } else if (viewChannelId in this.channelLookup) {
                this.setFocusedChannel(this.channelLookup[viewChannelId]);
            } else {
                this.setFocusedChannel(null);
            }
            this.cdr.markForCheck();
        });

        this.urlSubscription = this.route.url.subscribe((url: UrlSegment[]) => {
            this.advancedDetails = url.length && url.at(url.length - 1).toString() == 'advanced';

            if (this.advancedDetails) {
                this.viewMode = this.VIEWMODE_ADVANCED_CHANNEL;
            } else {
                if (this.selectedGroup) {
                    this.viewMode = this.VIEWMODE_CHANNELS;
                } else {
                    this.viewMode = this.VIEWMODE_GROUPS;
                }
            }

            const findChannelId = Number(url[2]?.path);
            if (findChannelId) {
                for (let i = 0; i < this.channels.length; i++) {
                    if (this.channels[i].fcuData.fcuChannelId === findChannelId) {
                        this.selectedChannel = this.channels[i];
                    }
                }
            } else {
                this.selectedChannel = null;
            }
            this.cdr.markForCheck();
        });

        this.currentUserSubjectSubscription = this.authService.currentUser$.subscribe(async () => {
            if (this.deleteConfirmOverlay?.style.display != 'none')
                this.deleteConfirmOverlay.emitValue(false);
            await this.setCustomGroups();
        });
        this.customGroupSocketService.updateListSubject().subscribe(async (groupId: string) => {
            if (groupId && this.selectedGroupId == groupId) {
                await this.onSubmitCustomGroup(groupId);
                this.setSelectedGroup(groupId);
                this.cdr.markForCheck();
                return;
            }
            await this.setCustomGroups();
        });

        this.subscritptions.push(this.parameterSubscription);
        this.subscritptions.push(this.urlSubscription);
        this.subscritptions.push(this.currentUserSubjectSubscription);
        this.subscritptions.push(this.accessUpdatedSubscription);

        this.hasFinishedLoading = true;
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.customGroupSocketService.destroySubscriptions();
        this.subscritptions.forEach((sub) => sub?.unsubscribe());
    }

    static CanDeactivate(component: OpsChannelViewsComponent): boolean | Observable<boolean> {
        if (component.viewMode !== component.VIEWMODE_NEW_CUSTOM_GROUP) {
            return true;
        }

        if (component.newCustomGroup && !component.newCustomGroup.hasUnsavedChanges()) {
            return true;
        }

        component.exitConfirmOverlay.setVisible(true);
        component.cdr.markForCheck();
        const shouldClosePage = component.exitConfirmOverlay.onOptionSelected.asObservable();
        return shouldClosePage;
    }

    assignToLookup(
        channel: Channel,
        lookup: { [fcuChannelId: number]: Channel } = this.channelLookup,
    ) {
        lookup[channel.fcuData.fcuChannelId] = channel;
    }

    setFocusedChannel(channel: Channel) {
        this.focusedChannel = channel;
        this.cdr.markForCheck();
    }

    getFocusedChannelValue() {
        return this.flagService.getFlags(this.focusedChannel)?.value;
    }

    async loadChannelData() {
        const [groups, channels, customGroups, customGroupChannels] = await Promise.all([
            lastValueFrom(this.groupService.getGroups()),
            lastValueFrom(this.channelService.getChannels()),
            this.customGroupsService.getGroups(),
            this.customGroupsService.getAllChannelsCustomGroups(),
        ]);

        const lookup = {};

        for (let i = 0; i < channels.length; i++) {
            this.assignToLookup(channels[i], lookup);
        }

        this.customGroups = customGroups;
        await this.setCustomGroups();
        this.customGroupChannels = customGroupChannels;
        this.groups = groups;
        this.channels = channels;
        this.channelLookup = lookup;
    }

    async setCustomGroups() {
        const [customGroups, customGroupChannels] = await Promise.all([
            this.customGroupsService.getGroups(),
            this.customGroupsService.getAllChannelsCustomGroups(),
        ]);
        this.customGroups = customGroups;
        this.customGroupChannels = customGroupChannels;
        this.filteredCustomGroups = [];
        const currentUserId = this.authService.getCurrentUserId();
        if (!currentUserId) {
            this.customGroups.map((customGroup) => {
                if (customGroup.publicAccess) {
                    this.filteredCustomGroups.push(customGroup);
                }
            });
        } else {
            this.customGroups.map((customGroup) => {
                if (customGroup.publicAccess) {
                    this.filteredCustomGroups.push(customGroup);
                } else if ((customGroup as any).AccessCustomGroups.length > 0) {
                    (customGroup as any).AccessCustomGroups.map((usersAccess) => {
                        if (
                            usersAccess.roleId === currentUserId &&
                            usersAccess.accessLevel !== AccessType.NoAccess
                        ) {
                            this.filteredCustomGroups.push(customGroup);
                        }
                    });
                }
            });
        }

        if (this.customGroup) {
            let selectedGroupsIsActive = false;
            this.filteredCustomGroups.map((customGroup) => {
                if (this.selectedGroupId === customGroup.id) {
                    this.selectedGroup = customGroup;
                    selectedGroupsIsActive = true;
                    if (this.customGroupData) {
                        this.onBackCustomGroup(customGroup.id);
                    }
                }
            });
            if (!selectedGroupsIsActive) {
                this.onChannelListBack();
            }
        }
        this.cdr.markForCheck();
    }

    onChannelUpdateClicked(channel: Channel) {
        this.updateChannel(channel).then(() => this.notificationService.channelUpdateComplete());
        this.cdr.markForCheck();
    }

    onAlarmDisableClicked(channel: Channel) {
        this.updateChannel(channel);
        this.cdr.markForCheck();
    }

    async updateChannel(channel: Channel) {
        try {
            await lastValueFrom(this.channelService.update(channel));
            const updatedChannel = await lastValueFrom(this.channelService.get(channel.id));
            this.replaceChannel(updatedChannel);

            if (this.channelSettingsForm) {
                this.channelSettingsForm.closePreviewForm();
            }
            if (this.channelSettingsListForm) {
                this.channelSettingsListForm.closePreviewForm();
            }
        } catch (e) {
            if (e.status == HttpStatusCode.Unauthorized) {
                this.authService.signOut();
                this.authService.showSignInOverlay();
                const loggedInUser = await firstValueFrom(
                    this.authService.currentUser$.pipe(skip(1), first()),
                );
                if (loggedInUser.token && loggedInUser.userId) {
                    this.updateChannel(channel);
                }
            } else {
                console.error(e);
                throw e;
            }
        }
    }

    handleUpdateChannelList(udpatedChannel) {
        const index = this.channels.findIndex((c) => c.id == udpatedChannel.id);
        if (index !== -1) {
            this.channels[index] = udpatedChannel;
        }
        this.notificationService.channelUpdateComplete();
    }

    replaceChannel(newChannel: Channel) {
        this.assignToLookup(newChannel);

        const index = this.channels.findIndex((c) => c.id == newChannel.id);
        if (index !== -1) {
            this.channels[index] = newChannel;
        }

        const displayIndex = this.displayChannels.findIndex((c) => c.id == newChannel.id);
        if (displayIndex !== -1) {
            this.displayChannels[index] = newChannel;
        }

        if (this.focusedChannel) {
            if (this.focusedChannel.id == newChannel.id) {
                this.focusedChannel = newChannel;
            }
        }
    }

    onChannelSelected(channel: Channel) {
        this.navigateToChannel(channel);
    }

    onAdvancedChannelDetails(channel: Channel) {
        this.navigateToChannelAdvancedPage(channel);
    }

    onAdvancedDetailsClosed() {
        this.navigateToChannel(this.focusedChannel);
    }

    onAdvancedChannelDetailsSubmit(submission: ChannelAdvancedSettingSubmission) {
        this.updateChannel(submission.channel)
            .then((ch) => {
                this.notificationService.channelUpdateComplete();
            })
            .catch((err) => {
                this.notificationService.channelUpdateFailed();
            });

        const graphPromises = Array(submission.modifiedGraphs.length);
        for (let i = 0; i < submission.modifiedGraphs.length; i++) {
            graphPromises[i] = this.updateTrendGraph(submission.modifiedGraphs[i]);
        }

        Promise.all(graphPromises).catch((err) => {
            console.error(err);
            this.notificationService.trendGraphUpdateFailed();
        });
    }

    updateTrendGraph(graph: TrendGraphView) {
        return this.trendGraphService.update(graph);
    }

    navigateToChannel(channel: Channel) {
        this.router.navigate([
            '/ops',
            'groups',
            this.selectedGroup.id,
            'channels',
            channel.fcuData.fcuChannelId,
        ]);
    }

    navigateToChannelAdvancedPage(channel: Channel) {
        this.router.navigate([
            '/ops',
            'groups',
            this.selectedGroup.id,
            'channels',
            channel.fcuData.fcuChannelId,
            'advanced',
        ]);
    }

    onChannelListBack() {
        this.router.navigate(['/ops', 'groups']);
    }

    async onCloseChannelDetails() {
        await this.router.navigate(['/ops', 'groups', this.selectedGroup.id, 'channels']);
    }

    onCreateGraph(graph: TrendGraphView) {
        this.navigation.toTrendGraphCreate(graph);
    }

    advancedFormHasUnsavedChanges() {
        return (
            this.advancedChannelSettingsForm && this.advancedChannelSettingsForm.hasUnsavedChanges()
        );
    }

    promptConfirmExitAdvancedSettings() {
        this.confirmationPromptService.showPrompt({
            mainLabel: this.translate.instant(
                'forms.channel.advanced.prompts.exit-without-saving.main-label',
            ),
            confirmLabel: this.translate.instant(
                'forms.channel.advanced.prompts.exit-without-saving.confirm',
            ),
            cancelLabel: this.translate.instant(
                'forms.channel.advanced.prompts.exit-without-saving.cancel',
            ),
        });
        return this.confirmationPromptService.onOptionSelected();
    }

    async onBackCustomGroup(groupId) {
        this.customName = null;
        this.customGroupData = null;
        this.viewMode = this.VIEWMODE_GROUPS;
        if (groupId) {
            await this.onSubmitCustomGroup(groupId);
            this.setSelectedGroup(groupId);
            this.viewMode = this.VIEWMODE_CHANNELS;
            this.selectedGroupId = groupId;
        } else if (this.selectedGroup) {
            this.router.navigate(['/ops', 'groups', this.selectedGroup.id, 'channels']);
            this.cdr.markForCheck();
            this.viewMode = this.VIEWMODE_CHANNELS;
        }
    }

    editCustomGroup(channelList) {
        this.customGroupData = {
            id: this.selectedGroup.id,
            groupId: this.selectedGroup.id,
            channels: channelList,
            groupName: this.selectedGroup.displayName,
            description: this.selectedGroup.description,
            publicAccess: this.selectedGroup.publicAccess,
        };
        this.viewMode = this.VIEWMODE_NEW_CUSTOM_GROUP;
        this.cdr.markForCheck();
    }

    async onCreateGroup() {
        if (this.channelSettingsForm) {
            await this.onCloseChannelDetails();
        }
        this.viewMode = this.VIEWMODE_NEW_CUSTOM_GROUP;
        this.cdr.markForCheck();
    }

    handleDeleteGroup() {
        this.deleteConfirmOverlay.setVisible(true);
        const onDeleteConfirm = this.deleteConfirmOverlay.onOptionSelected.subscribe(
            (v: boolean) => {
                onDeleteConfirm.unsubscribe();
                if (v) {
                    this.customGroupsService.deleteGroup(this.selectedGroup.id);
                    this.onChannelListBack();
                }
            },
        );
    }

    async onSubmitCustomGroup(groupId?) {
        await this.setCustomGroups();
        if (groupId) {
            this.router.navigate(['/ops', 'groups', groupId, 'channels']);
        }
    }

    setSelectedGroup(groupId) {
        if (groupId) {
            this.selectedGroup = this.groups.find((g) => g.id == groupId);

            if (this.selectedGroup != null) {
                this.displayChannels = this.channels.filter((c) => c.groupId == groupId);
                this.customGroup = false;
                this.displayChannels.sort((a, b) => a.rowIndex - b.rowIndex);
            } else {
                this.selectedGroup = this.customGroups.find((g) => g.id == groupId);

                let showChannelIds = new Set(
                    this.customGroupChannels
                        .filter((channelGroup) => channelGroup.customGroupId === groupId)
                        .map((channelGroup) => channelGroup.channelId),
                );

                this.displayChannels = this.channels.filter((c) => showChannelIds.has(c.id));

                this.displayChannels.sort(
                    (a, b) => a.fcuData.fcuChannelId - b.fcuData.fcuChannelId,
                );
                this.customGroup = true;
            }
        } else {
            this.selectedGroup = null;
        }

        this.cdr.markForCheck();
    }
}

export function CanAdvancedChannelViewsDeactivate(
    component: OpsChannelViewsComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot,
): boolean | Observable<boolean> {
    const canExit =
        component.viewMode == component.VIEWMODE_ADVANCED_CHANNEL &&
        component.advancedFormHasUnsavedChanges()
            ? component.promptConfirmExitAdvancedSettings()
            : true;
    return canExit;
}
